<template>
    <div class="container px-1 py-5">
        <div class="flex flex-col items-end justify-between lg:flex-row lg:items-center mb-10" dusk="part-numbers-header">
            <div class="flex flex-col justify-between lg:justify-start lg:mb-0 mb-5 md:flex-row md:items-center w-full">
                <h1 class="lg:text-3xl mb-5 md:mb-0 mr-10 text-2xl">{{ __('Part numbers') }}</h1>

                <c-control-text
                    class="grow md:max-w-[300px]"
                    icon="search"
                    name="search"
                    v-bind:placeholder="__('Search by make, model or P/N')"
                    v-model="s"
                    v-on:keyup="search"
                />
            </div>

            <div class="flex items-center shrink-0">
                <c-button class="bg-highlight rounded-br-none rounded-tr-none shrink-0 text-white"
                          dusk="create-pn-button"
                          router
                          v-if="can('devices.store')"
                          v-bind:to="{ name : 'devices.pn.create' }"
                >
                    + {{ __('Add part number') }}
                </c-button>
                <c-button class="bg-highlight rounded-br-none rounded-tr-none shrink-0 text-white"
                          dusk="create-pn-button"
                          v-if="!can('devices.store') && can('devices.import')"
                >
                    + {{ __('Import from XLS') }}
                </c-button>
                <div class="after:absolute after:border-[5px] after:border-transparent after:border-t-white after:content-[''] after:h-0 after:mt-1 after:left-1/2 after:pointer-events-none after:top-1/2 after:-translate-x-1/2 after:-translate-y-1/2 after:w-0 px-4 bg-highlight border-l border-indigo-800 cursor-pointer h-9 hover:bg-indigo-700 py-2.5 relative rounded-br rounded-tr"
                     v-if="can('devices.import|devices.store')"
                     v-on:click="showAddMenu = !showAddMenu"
                     v-on-click-outside="closeAddMenu"
                >
                    <nav class="absolute bg-white border border-slate-200 flex flex-col min-w-[150px] mt-2 right-0 rounded shadow-sm text-sm top-full whitespace-nowrap z-10"
                         v-show="showAddMenu"
                    >
                        <router-link class="flex border-b border-slate-200 items-center p-2"
                                     v-bind:to="{ name : 'devices.pn.create' }"
                                     v-if="can('devices.store')"
                        >
                            <c-icon class="h-4 mr-2 stroke-slate-800 w-4" icon="plus" />{{ __('Add part number') }}
                        </router-link>
                        <a class="flex hover:bg-slate-50 items-center p-2"
                           v-bind:href="'/qr/downloadAll/' + sessionStore.user?.tenant.id"
                        >
                            <c-icon class="h-4 mr-2 stroke-slate-800 w-4" icon="printer" />
                            {{ __('Print QR codes') }}
                        </a>
                        <a class="flex hover:bg-slate-50 items-center p-2"
                           v-if="can('devices.import')"
                           v-on:click="showImportDialog"
                        >
                            <c-icon class="h-4 mr-2 stroke-slate-800 w-4" icon="file-plus" />
                            {{ __('Import from XLS') }}
                        </a>
                        <input class="hidden" id="import-dialog" type="file"
                               v-on:change="importFile"
                        />
                        <a class="flex hover:bg-slate-50 items-center p-2"
                           v-if="can('devices.import')"
                           v-bind:href="'/templates/' + langStore.locale + '/devices/pn'"
                        >
                            <c-icon class="h-4 mr-2 stroke-slate-800 w-4" icon="arrow-down" />
                            {{ __('Download XLS template') }}
                        </a>
                    </nav>
                </div>
            </div>
        </div>
        <ul class="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4" dusk="part-numbers-list" v-if="pns.length > 0">
            <li class="bg-white border border-slate-200 group/item flex items-center leading-tight relative rounded shadow-sm"
                v-for="(pn, key) in pns"
            >
                <router-link class="flex flex-col h-16 justify-center pl-2 pr-8 py-2 w-full"
                             v-bind:to="{ name : 'devices.pn.edit', params : { pn : pn.id } }"
                             v-if="can('devices.show|devices.update')"
                >
                    <span class="font-semibold line-clamp-1 mb-1" v-if="pn.make || pn.model">
                        {{ pn.make }} {{ pn.model }}
                    </span>
                    <span class="overflow-hidden text-ellipsis text-sm text-slate-500 whitespace-nowrap">
                        {{ pn.pn }}
                    </span>

                    <span class="absolute cursor-help right-1 top-1"
                          v-if="pn.needs_maintenance"
                          v-on:mouseover="pnTooltip = pn.id"
                          v-on:mouseout="pnTooltip = null"
                    >
                        <c-icon class="h-6 stroke-error w-6" icon="alert-triangle" />
                        <c-tooltip position="top" v-bind:active="pnTooltip === pn.id">
                            {{ __('At least one device needs maintenance.') }}
                        </c-tooltip>
                    </span>
                </router-link>

                <div class="absolute -bottom-1 group/card-actions right-1"
                     dusk="kebab-menu"
                     v-if="can('devices.destroy|devices.update')"
                >
                    <span class="cursor-pointer flex h-6 hover:bg-slate-200 items-center justify-center mb-2 rounded w-6"
                          dusk="part-number-actions"
                          v-on:click="showCardActions = []; showCardActions[pn.id] = !showCardActions[pn.id]"
                          v-on-click-outside="key === 0 ? closeCardActions : [() => {}]"
                    >
                        <c-icon class="h-5 pointer-events-none stroke-slate-800 w-5" icon="more-vertical" />
                    </span>
                    <div class="absolute bg-slate-700 border border-slate-200 bottom-0 hidden overflow-hidden right-0 rounded shadow-sm translate-y-full z-10"
                         v-bind:class="{ '!block' : showCardActions[pn.id] }"
                    >
                        <router-link class="border-b border-b-slate-500 flex hover:bg-slate-800 items-center p-2 text-white text-xs whitespace-nowrap"
                                     dusk="edit-part-number-button"
                                     v-bind:to="{ name : 'devices.pn.edit', params : { pn : pn.id } }"
                                     v-if="can('devices.update')"
                        >
                            <c-icon class="h-4 mr-2 stroke-white w-4" icon="edit-3" />{{ __('Edit') }}
                        </router-link>
                        <a class="cursor-pointer flex hover:bg-slate-800 items-center p-2 text-red-300 text-xs whitespace-nowrap"
                           dusk="delete-part-number-button"
                           v-if="can('devices.destroy')"
                           v-on:click="deletedPn = pn.id"
                        >
                            <c-icon class="h-4 mr-2 stroke-red-300 w-4" icon="trash-2" />{{ __('Delete') }}
                        </a>
                    </div>
                </div>
            </li>
        </ul>
        <div class="flex flex-col items-center justify-center text-center" v-else>
            <img class="mb-5" src="/img/empty-state.png" srcset="/img/empty-state.png 2x"
                 v-bind:alt="__('There are no part numbers available.')"
            />
            <div class="flex flex-col items-center justify-center text-center">
                <span class="font-semibold mb-2 text-center text-lg text-slate-500">{{ __('There are no part numbers available.') }}</span>
                <router-link class="text-center text-sm text-highlight"
                             v-bind:to="{ name : 'devices.pn.create' }"
                             v-if="can('devices.store')"
                >
                    + {{ __('Add part number') }}
                </router-link>
            </div>
        </div>
    </div>
    <c-modal class="md:w-[450px]" v-bind:open="deletedPn !== null" v-bind:title="__('Delete part number')" v-on:closeModal="deletedPn = null">
        <template v-slot:body>
            <div class="py-5 text-center text-sm">
                {{ __('Are you sure you want to delete this part number?') }}
            </div>
        </template>
        <template v-slot:footer>
            <div class="flex justify-end w-full">
                <c-button class="bg-slate-100 mr-2 text-slate-800" v-on:click="deletedPn = null">
                    {{ __('Cancel') }}
                </c-button>
                <c-button class="bg-error text-white" dusk="delete-part-number-modal-button" v-on:click="del">
                    {{ __('Delete') }}
                </c-button>
            </div>
        </template>
    </c-modal>
</template>

<script setup lang="ts">
    import { onMounted, ref } from 'vue';
    import { vOnClickOutside } from '@vueuse/components';
    import debounce from 'debounce';
    import {
        CButton,
        CControlText,
        CIcon,
        CModal,
        CTooltip,
    } from '@teamfurther/cinderblock';

    import PartNumberRepository from '../../../repositories/PartNumberRepository';
    import { useLocalizationStore } from '../../../stores/localization';
    import { useSessionStore } from '../../../stores/session';

    const closeCardActions = [
        (e) => {
            if (
                !e.target.parentNode.className?.toString().includes('group/card-actions')
            ) {
                showCardActions.value = [];
            }
        }
    ];

    const deletedPn = ref(null);
    const langStore = useLocalizationStore();
    const partNumberRepository = PartNumberRepository.getInstance();
    const pns = ref<Object[]>([]);
    const pnTooltip = ref<string | null>(null);
    const s = ref<string>('');

    const search = debounce(async () => {
        pns.value = await partNumberRepository.index({
            filters: {
                search : s.value,
            },
        });
    }, 500);

    const sessionStore = useSessionStore();
    const showAddMenu = ref<boolean>(false);
    const showCardActions = ref<boolean[]>([]);

    function closeAddMenu() {
        showAddMenu.value = false;
    }

    async function del() {
        await partNumberRepository.destroy(deletedPn.value);

        let pnsTemp = pns.value;
        pns.value.forEach((pn, key) => {
            if (pn.id === deletedPn.value) {
                pnsTemp.splice(key, 1);
            }
        });
        pns.value = pnsTemp;

        deletedPn.value = null;
    }

    async function importFile(e)
    {
        const input: HTMLInputElement = (<HTMLInputElement>e.target);

        if (input && input.files) {
            const data: FormData = new FormData();
            data.append('file', input.files[0]);

            await partNumberRepository.import(data);
        } else {
            console.error('Request: no file found.')
        }
    }

    function showImportDialog() {
        showAddMenu.value = false;

        document.querySelector('#import-dialog').click();
    }

    onMounted(async () => {
        pns.value = await partNumberRepository.index();
    });
</script>
